import { useCallback, useId, useState } from "react";

import { Copy, DoubleArrowsCircle, Eye, EyeCrossed } from "components/icons/generated";
import useCopyToClipboard from "hooks/useCopyToClipboard";

import Box from "../Box";
import ButtonIcon from "../ButtonIcon";
import Typography from "../Typography";
import { showRefreshSecretConfirmationModal } from "./refreshSecretConfirmationModal";

type SecretFieldDetailsProps = {
  secret: string;
  avoidCopy?: boolean;
  regenerateSecret?: () => Promise<boolean>;
};

const SecretFieldDetails = ({ secret, avoidCopy, regenerateSecret }: SecretFieldDetailsProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const valueId = useId();

  const copySecret = useCopyToClipboard(secret, () => setIsVisible(false));
  const showRegenerateModal = useCallback(() => {
    if (regenerateSecret) {
      showRefreshSecretConfirmationModal({
        onConfirm: regenerateSecret,
      });
    }
  }, [regenerateSecret]);

  return (
    <Box direction="row" gap="medium" justify="between">
      <Typography id={valueId} tag="span" variant="p-body2" wordBreak>
        {isVisible ? (
          secret
        ) : (
          <>
            <span aria-hidden="true">
              &bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;
            </span>
            <span className="sr-only">Secret hidden</span>
          </>
        )}
      </Typography>
      <Box direction="row" gap="medium" shrink="0" align="center">
        {!avoidCopy && (
          <ButtonIcon onPress={copySecret} icon={Copy} variant="ghost">
            Copy secret value
          </ButtonIcon>
        )}
        {regenerateSecret && (
          <ButtonIcon onPress={showRegenerateModal} icon={DoubleArrowsCircle} variant="ghost">
            Regenerate secret
          </ButtonIcon>
        )}
        <ButtonIcon
          onPress={() => setIsVisible(!isVisible)}
          icon={isVisible ? EyeCrossed : Eye}
          variant="ghost"
          aria-controls={valueId}
          aria-pressed={isVisible}
        >
          {isVisible ? "Hide secret value" : "Show secret value"}
        </ButtonIcon>
      </Box>
    </Box>
  );
};

SecretFieldDetails.displayName = "DS.SecretFieldDetails";

export default SecretFieldDetails;
